/**
 * Whether this browser has agreed to analytics cookies.
 *
 * Google Analytics is the first thing FloorPoll ships that stores something
 * for its own benefit rather than the visitor's. The five entries the Privacy
 * Policy enumerates are all functional — a sign-in token, a device token, the
 * picks a phone made — and the page shows no consent banner on the strength of
 * that. Analytics storage is not functional, so under ePrivacy it needs asking
 * first, and this module is the record of the answer.
 *
 * **Absent is not denied, and the difference drives the UI.** Three states,
 * not two: `granted`, `denied`, and undefined meaning nobody has been asked.
 * Only the third shows the banner. Collapsing undefined into `denied` would
 * behave identically for gtag — both withhold storage — and would make the
 * banner either permanent or impossible, depending on which way the collapse
 * went.
 *
 * Reads and writes are wrapped because a browser in private mode, or one with
 * site data blocked, throws on access rather than returning null. A visitor
 * who cannot be asked is a visitor who has not consented, which is the same
 * answer as never having been asked, so the failure mode is the safe one
 * without any special casing.
 */

const STORAGE_KEY = 'floorpoll.analyticsConsent';

export type ConsentChoice = 'granted' | 'denied';

const isChoice = (value: string | null): value is ConsentChoice =>
  value === 'granted' || value === 'denied';

/** The stored answer, or undefined when this browser has not been asked. */
export function readConsent(): ConsentChoice | undefined {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isChoice(stored) ? stored : undefined;
  } catch {
    // Storage unavailable. Undefined is honest here: we genuinely do not know
    // this visitor's answer, and cannot remember one if they give it.
    return undefined;
  }
}

/**
 * Records an answer.
 *
 * A write that fails is swallowed rather than surfaced. The caller has already
 * applied the choice to gtag by the time this runs, so the visitor's decision
 * is honoured for this page either way; all that is lost is remembering it,
 * and the only consequence is being asked again next visit.
 */
export function writeConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* ignore — see above */
  }
}

/** Forgets the answer, so the banner asks again. Used by the Privacy page. */
export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore — see above */
  }
}
